import * as React from 'react';
import { useCallback } from 'react';
import { createContext, useContext, useContextSelector } from 'use-context-selector';

import { FlightTurnaroundDto, Resource, ResourceAllocation, ResourceType } from '../../../backend';
import { useOptimizedState } from '../../../hooks/useOptimizedState';
import { ViewModeFormValues } from '../RmsHeader/ViewMode/ViewModeForm';

export interface RmsGanttState {
    rmsFilters: ViewModeFormValues;
    resourcesByType: Resource[];
    day: Date;
    resourceType: ResourceType;
    selectedTurnarounds: FlightTurnaroundDto[];
    mousePositionY: number;
    isFocused: boolean;
    isDragging: boolean;
    dayBounds: { from: number; to: number };
    proposedAllocation: ResourceAllocation[] | null;
    nbUpdateAllocations: number;
}

export const initialRmsGanttState: RmsGanttState = {
    rmsFilters: {
        viewModeScheduled: 'deviations',
        resourceFilterCriteria: null,
    },
    resourcesByType: [],
    day: new Date(),
    resourceType: ResourceType.PARKING,
    // selection one turnaround with different resources -> need to save both turnarounds
    selectedTurnarounds: [],
    mousePositionY: 0,
    isFocused: false,
    isDragging: false,
    dayBounds: { from: null, to: null },
    proposedAllocation: null,
    nbUpdateAllocations: 0,
};

export interface RmsGanttContextState {
    state: RmsGanttState;
    setState: React.Dispatch<React.SetStateAction<RmsGanttState>>;
}

const initialValue: RmsGanttContextState = {
    state: initialRmsGanttState,
    setState: () => {},
};

export const RmsGanttContext = createContext<RmsGanttContextState>(initialValue);

export const useRmsGanttContext = () => useContext(RmsGanttContext);

const useRmsGanttSet = () => useContextSelector(RmsGanttContext, state => state.setState);

export const useRmsGanttValue = <Key extends keyof RmsGanttState>(key: Key): RmsGanttState[Key] =>
    useContextSelector(RmsGanttContext, state => state.state[key]);

export const useSetRmsGantt = <Key extends keyof RmsGanttState>(key: Key) => {
    const set = useRmsGanttSet();

    return useCallback(
        (value: React.SetStateAction<RmsGanttState[Key]>) =>
            set(v => {
                const newValue = typeof value === 'function' ? (value as any)(v[key]) : value;

                return {
                    ...v,
                    [key]: newValue,
                };
            }),
        [key, set],
    );
};

export const useSetRmsGanttValue = <Key extends keyof RmsGanttState>(
    key: Key,
    value: React.SetStateAction<RmsGanttState[Key]>,
) => {
    const set = useSetRmsGantt(key);

    return useCallback(() => set(value), [set, value]);
};

export const useRmsGanttState = <Key extends keyof RmsGanttState>(
    key: Key,
): [RmsGanttState[Key], React.Dispatch<React.SetStateAction<RmsGanttState[Key]>>] => [
    useRmsGanttValue(key),
    useSetRmsGantt(key),
];

export const RmsGanttContextProvider = ({
    children,
    config,
}: {
    children: React.ReactNode;
    config?: Partial<RmsGanttState>;
}) => {
    const [state, setState] = useOptimizedState<RmsGanttState>({ ...initialRmsGanttState, ...config });

    return <RmsGanttContext.Provider value={{ state: { ...state }, setState }}>{children}</RmsGanttContext.Provider>;
};
